/** Presentation helpers shared by the ported views. */

const NUMBER = new Intl.NumberFormat('en-US')
const CREDITS = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 })

export function formatInt(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : NUMBER.format(value)
}

/**
 * Credits, with a decimal only when there is one to show.
 *
 * Rates produce fractions — a delete costs 0.5 — so the decimal cannot be
 * dropped outright. But a minimum of one place rendered every whole figure as
 * "100.0", "0.0" and "25,000.0", which is what the dashboard, the plan cards and
 * the top-up balance were all showing.
 */
export function formatCredits(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : CREDITS.format(value)
}

export function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleTimeString('en-US', { hour12: false })
}

/** "2 min ago" style stamps, matching the prototype's Recent jobs column. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const seconds = Math.round((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`
  if (seconds < 172800) return 'yesterday'
  return formatDateTime(iso)
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function initials(name: string | null | undefined, email: string): string {
  const source = (name ?? '').trim() || email
  const parts = source.split(/[\s@._-]+/).filter(Boolean)
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase()
}
